import { isCapacitorNative } from "@/lib/capacitor"

/** Opt out of dismiss-on-Enter (e.g. AI chat composer — Enter inserts a newline). */
export const ENTER_NEWLINE_ATTR = "data-enter-newline"

/**
 * Modal/data-entry forms where Enter should dismiss the keyboard, not submit.
 * Search/filter forms must NOT use this attribute.
 */
export const FORM_ENTER_DISMISS_ATTR = "data-form-enter-dismiss"

/** Spread onto `<form>` or a wrapper around form fields. */
export const formEnterDismissProps = { [FORM_ENTER_DISMISS_ATTR]: "" } as const

function allowsEnterNewline(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.closest(`[${ENTER_NEWLINE_ATTR}]`) != null
}

function isInsideEnterDismissForm(target: HTMLElement): boolean {
  return target.closest(`[${FORM_ENTER_DISMISS_ATTR}]`) != null
}

function isDismissibleInput(target: HTMLElement): target is HTMLInputElement {
  if (!(target instanceof HTMLInputElement)) return false
  const type = target.type.toLowerCase()
  return type !== "button" && type !== "submit" && type !== "checkbox" && type !== "radio"
}

function dismissField(target: HTMLElement, e: KeyboardEvent): void {
  e.preventDefault()
  target.blur()
}

/**
 * Enter in marked modal forms dismisses the keyboard without submitting (all platforms).
 * On Capacitor native, Enter also dismisses the keyboard elsewhere except `[data-enter-newline]`.
 */
export function handleEnterKey(e: KeyboardEvent): void {
  if (e.key !== "Enter" || e.isComposing) return

  const target = e.target
  if (!(target instanceof HTMLElement)) return
  if (allowsEnterNewline(target)) return

  const inDismissForm = isInsideEnterDismissForm(target)

  if (target instanceof HTMLTextAreaElement) {
    if (e.shiftKey) return
    if (inDismissForm || isCapacitorNative()) {
      dismissField(target, e)
    }
    return
  }

  if (isDismissibleInput(target)) {
    if (inDismissForm) {
      dismissField(target, e)
      return
    }
    if (isCapacitorNative()) {
      target.blur()
    }
  }
}

/** @deprecated Use handleEnterKey */
export const handleNativeEnterKey = handleEnterKey

export function attachEnterKeyListener(): () => void {
  const onKeyDown = (e: KeyboardEvent) => handleEnterKey(e)
  document.addEventListener("keydown", onKeyDown, true)
  return () => document.removeEventListener("keydown", onKeyDown, true)
}

/** @deprecated Use attachEnterKeyListener */
export const attachMobileEnterKeyListener = attachEnterKeyListener
